<script lang="ts" setup>
import ModalShipmentItemGallery from '~/components/SitePro/Modals/ModalShipmentItemGallery.vue'
import ModalTrackings from '~/components/SitePro/Modals/ModalTrackings.vue'
import { useSiteProContentStore } from '~/store/shipzeeProContentStore'
import { useLoaderStore } from '~/store/loader'
import type { ForwardRequestDataWarehouseItemInterface } from '~/types/shipzeePro/shipzeeProTypes'
import type { AllPackageInterface } from '~/types/shipzeePro/packagesTypes'
import AllShipmentsTableRow from '~/components/SitePro/Tables/AllShipmentsTableRow.vue'

const { t } = useLang()
const shipzeeProContent = useSiteProContentStore()
const loader = useLoaderStore()
const perPage = 6

const currentPageItems = computed(() =>
  shipzeeProContent.allItems.slice(
    shipzeeProContent.allItemsCurrentPage * perPage - perPage,
    shipzeeProContent.allItemsCurrentPage * perPage
  )
)

const openedPackages = ref<AllPackageInterface[]>([])

const isModalVisible = ref(false)
const isTrackingsModalVisible = ref(false)

const itemToShowGallery = ref<ForwardRequestDataWarehouseItemInterface | null>(null)
const trackingsToShow = ref<AllPackageInterface>()

const areAllPackagesOpened = computed(() => {
  let allOpened = true
  currentPageItems.value.forEach((item: AllPackageInterface) => {
    if (!openedPackages.value.includes(item)) {
      allOpened = false
    }
  })

  return allOpened
})

const showOpenAllButton = computed(() =>
  currentPageItems.value.some((shipmentPackage: AllPackageInterface) => shipmentPackage.packages.length)
)

function toggleOpenAll() {
  loader.setLoader(true)
  const areAllOpened = areAllPackagesOpened.value
  openedPackages.value = openedPackages.value.filter((pkg) => !currentPageItems.value.includes(pkg))

  if (!areAllOpened) {
    currentPageItems.value.forEach(async (item: AllPackageInterface) => {
      if (!item.fr_data) {
        item.fr_data = await $fetch('/api/site-pro/get-forward-request-by-id', {
          method: 'POST',
          body: { id: item.id },
        })

        shipzeeProContent.updateAllItem(item)
      }
    })
    openedPackages.value = openedPackages.value.concat(currentPageItems.value)
  }
  loader.setLoader(false)
}

async function toggleOpenRow(shipment: AllPackageInterface) {
  loader.setLoader(true)
  if (openedPackages.value.includes(shipment)) {
    openedPackages.value = openedPackages.value.filter(
      (shipmentPackage: AllPackageInterface) => shipmentPackage.id !== shipment.id
    )
  } else {
    await loadFrData(shipment)
    openedPackages.value.push(shipment)
  }
  loader.setLoader(false)
}

async function loadFrData(shipment: AllPackageInterface) {
  if (!shipment.fr_data) {
    shipment.fr_data = await $fetch('/api/site-pro/get-forward-request-by-id', {
      method: 'POST',
      body: { id: shipment.id },
    })
    shipzeeProContent.updateAllItem(shipment)
  }
}

function showGalleryModal(shipmentPackage: ForwardRequestDataWarehouseItemInterface) {
  itemToShowGallery.value = shipmentPackage
  isModalVisible.value = true
}

async function showTrackingsModal(shipment: AllPackageInterface) {
  if (!shipment.fr_data) {
    try {
      loader.setLoader(true)
      await loadFrData(shipment)
    } finally {
      loader.setLoader(false)
    }
  }
  trackingsToShow.value = shipment
  isTrackingsModalVisible.value = true
}
</script>

<template>
  <div class="all-shipments-table-component">
    <div
      v-if="shipzeeProContent.allItems.length"
      class="pro-table xl:bg-white dark:bg-dmGray2 p-2 xl:p-5 rounded-b-[2rem] xl:rounded-r-[2rem]">
      <div class="table-desc text-sm pb-4 pt-5 xl:pt-0">
        {{ t('site_pro.all_shipments_below_are_ready_to_be_consolidated_and_shipped') }}
      </div>
      <div class="table-wrapper">
        <table class="all-shipments-table">
          <thead>
            <tr>
              <th>{{ t('site_pro.table.no') }}</th>
              <th>{{ t('site_pro.table.date') }}</th>
              <th>{{ t('site_pro.table.recipient') }}</th>
              <th>{{ t('site_pro.table.shipping_method') }}</th>
              <th>{{ t('site_pro.table.weight') }}</th>
              <th>{{ t('site_pro.table.sum') }}</th>
              <th>{{ t('site_pro.table.declared_value') }}</th>
              <th>{{ t('site_pro.table.tracking') }}</th>
              <th>{{ t('site_pro.table.status') }}</th>
              <th v-if="showOpenAllButton">
                <div>
                  <div
                    class="flex items-center justify-around w-10 h-10 bg-white dark:bg-dmGray1 rounded-full cursor-pointer wl:bg-neutral7"
                    @click="toggleOpenAll">
                    <i
                      class="fa-solid"
                      :class="{
                        'fa-chevron-up text-brand-primary': areAllPackagesOpened,
                        'fa-chevron-down': !areAllPackagesOpened,
                      }" />
                  </div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <AllShipmentsTableRow
              v-for="(shipment, index) in currentPageItems"
              :key="index"
              :shipment="shipment"
              :opened-packages="openedPackages"
              :current-page-items="currentPageItems"
              @toggle-open-row="toggleOpenRow"
              @show-trackings-modal="showTrackingsModal"
              @show-gallery-modal="showGalleryModal" />
          </tbody>
        </table>
      </div>

      <div class="all-shipments-table-mobile">
        <MobileAllShipmentsTableRow
          v-for="(shipment, index) in currentPageItems"
          :key="index"
          :shipment="shipment"
          :opened-packages="openedPackages"
          @toggle-open-row="toggleOpenRow"
          @show-trackings-modal="showTrackingsModal"
          @show-gallery-modal="showGalleryModal" />
      </div>

      <div v-if="shipzeeProContent.allItems.length" class="flex justify-center xl:justify-start wl:px-4">
        <Pagination
          :model-value="shipzeeProContent.allItemsCurrentPage"
          :items-total="shipzeeProContent.allItems.length"
          :items-per-page="perPage"
          class="mt-4"
          @update-model="shipzeeProContent.setAllItemsCurrentPage" />
      </div>
    </div>

    <LazyEmptyShipmentsTable v-else top-left-corner />

    <ModalShipmentItemGallery
      :visible="isModalVisible"
      :item="itemToShowGallery"
      @handle="(value) => (isModalVisible = value)" />
    <ModalTrackings
      :visible="isTrackingsModalVisible"
      :shipment="trackingsToShow"
      @load-fr-data="loadFrData"
      @handle="(value) => (isTrackingsModalVisible = value)" />
  </div>
</template>

<style lang="postcss" scoped>
.all-shipments-table-component {
  :deep(table) {
    @apply hidden xl:inline-block border rounded-md border-neutral6 dark:border-dmGray4 wl:m-0;

    th,
    td {
      @apply py-4;
    }

    td:nth-child(3),
    td:nth-child(4),
    td:nth-child(7) {
      @apply w-1/3;
    }

    tr {
      th:first-child,
      td:first-child {
        @apply pl-8;
      }

      th:last-child,
      td:last-child {
        @apply pr-8;
      }
    }

    @media screen and (max-width: 1400px) {
      tr {
        th,
        td {
          @apply px-1;
        }

        th:first-child,
        td:first-child {
          @apply pl-4;
        }

        th:last-child,
        td:last-child {
          @apply pr-4;
        }
      }
    }

    thead {
      @apply bg-neutral7 dark:bg-dmGray5;

      th {
        @apply align-middle text-text1 dark:text-dmText1 font-normal text-left text-sm border-0  bg-transparent;

        &:first-child {
          @apply rounded-tl-[2rem];
        }

        &:last-child {
          @apply rounded-tr-[2rem];
        }
      }
    }

    tbody {
      @apply bg-white dark:bg-dmGray1;

      td {
        @apply align-middle text-sm bg-white dark:bg-dmGray1;
      }

      tr:last-child {
        td {
          @apply border-none;

          &:first-child {
            @apply !rounded-bl-[2rem];
          }

          &:last-child {
            @apply !rounded-br-[2rem];
          }
        }
      }

      .collapsible-row {
        @apply bg-neutral7 dark:bg-dmGray5;
      }
    }
  }
}

:deep(.input-checkbox-component) {
  .formkit-inner {
    @apply bg-transparent;
  }

  .formkit-wrapper {
    margin-bottom: 0 !important;
  }
}

:deep(.pagination-items) {
  .pagination-item {
    @apply border-0 bg-transparent text-text1 dark:text-dmText1 p-2 min-w-[2rem];

    border-radius: 100%;
    font-size: 0.75rem;

    &.-active {
      @apply bg-neutral7 dark:bg-dmGray3 wl:bg-brand-primaryLight;
    }
  }

  .-previous,
  .-next {
    @apply text-brand-primary text-sm;

    &.-disabled {
      @apply text-neutral5;
    }
  }
}

.all-shipments-table-mobile {
  @apply xl:hidden text-sm text-text1 dark:text-dmText1;
}

.rounded {
  @apply rounded-[0.375rem];
}
</style>

<style lang="postcss" scoped>
.wl {
  .pro-table {
    @apply lg:px-0 lg:bg-neutral7 pb-0 rounded-md rounded-t-none pt-0;

    .ready-body,
    .shipments-pagination {
      @apply bg-white;

      :deep(.pagination-items) {
        .pagination-item {
          &.-active {
            @apply !bg-brand-primaryLight;
          }
        }
      }
    }

    .body-item {
      @apply border-x-0 !rounded-none;
    }

    .table-desc {
      @apply lg:mx-7 bg-neutral7 lg:bg-transparent px-4 lg:px-0 lg:border-b border-neutral5;
    }
  }
}

.wl {
  .all-shipments-table-component {
    .all-shipments-table {
      @apply lg:w-full;
    }

    .table-wrapper {
      @apply bg-white;
    }

    :deep(table) {
      @apply rounded-none m-0 border-none;

      thead tr {
        @apply border-none;

        th {
          border-radius: 0 !important;
        }
      }

      tbody {
        @apply border-none;
      }

      .body-item-row {
        @apply border-x-0;

        td {
          @apply border-none;
        }

        &:last-child {
          @apply rounded-none;

          .body-item-collapsible {
            @apply rounded-md;
          }

          td {
            @apply border-none;

            &:first-child {
              @apply rounded-none;
            }

            &:last-child {
              @apply rounded-none;
            }
          }
        }
      }
    }
  }
}
</style>
